import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { EMAIL_ENDPOINT } from '../data/links';
import { TOTAL_PROJECTS } from '../data/books';
import { tierFor } from '../data/tiers';
import type { Tier } from '../data/tiers';
interface GateProps {
  onUnlock: (email: string, tier: Tier) => void;
}
export function record(email: string, source: string, tier?: string) {
  if (!EMAIL_ENDPOINT) return;
  try {
    fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify({
        email,
        source,
        tier,
        at: new Date().toISOString()
      })
    }).catch(() => {});
  } catch {
    /* recording an email must never block the customer */
  }
}
export function Gate({
  onUnlock
}: GateProps) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError('We need your email to let you in and send you the new packs.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('That email address does not look right.');
      return;
    }

    /* A wrong code never blocks anybody. It simply means the basic kit, which
       is what almost everyone has, and the extras show as locked cards they
       can buy. Nobody is kept out of a product they paid for. */
    const tier = tierFor(code);
    setError(null);
    record(cleanEmail, 'gate', tier);
    onUnlock(cleanEmail, tier);
  };
  const fieldClass = 'w-full border-b bg-transparent px-0 py-2.5 font-sans text-[17px] text-ink placeholder:text-ink/35 transition-colors duration-150 ease-out focus:outline-none';
  return <main className="blueprint flex min-h-screen w-full flex-col justify-between bg-ground px-6 py-10 sm:px-10 sm:py-14" data-id="element-141" data-mp="1b5abv96bmy73">
      <header data-id="element-142" data-mp="1680pk2e8b2nh">
        <div className="flex items-baseline gap-3" data-id="element-143" data-mp="168a5sy9h1vfz">
          <span className="font-display text-[17px] font-semibold text-ink" data-id="element-144" data-mp="369i3yf7kh7g5">Little Engineer</span>
          <span aria-hidden="true" className="h-3 w-px bg-rule" data-id="element-145" data-mp="3b7jluwt9b0q1" />
          <span className="smallcaps font-sans text-[10px] text-gold" data-id="element-146" data-mp="3ba5ukzrvvm9u">Mega Kit</span>
        </div>
      </header>

      <div className="brackets mx-auto w-full max-w-[30rem] border border-rule bg-surface p-8 sm:p-10" data-id="element-147" data-mp="3k8q5her7crni">
        <p className="smallcaps font-sans text-[10px] font-semibold text-gold" data-id="element-148" data-mp="3p7udi22e4jc9">Your access</p>
        <h1 className="mt-3 font-display font-semibold text-[38px] leading-[1.05] text-ink sm:text-[46px]" data-id="element-149" data-mp="3u6xl9u9r93nx">
          The Workshop
        </h1>
        <p className="mt-4 max-w-measure font-sans text-[16px] leading-relaxed text-ink/65" data-id="element-150" data-mp="2tb12awz7e8f">
          Twelve books, {TOTAL_PROJECTS} projects, and a search that finds the right one in about
          four seconds. Leave your email and go straight in.
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-11" data-id="element-151" data-mp="grrhitkn4j3r">
          <div data-id="element-152" data-mp="gpwvtd1jomym">
            <label htmlFor="email" className="smallcaps block font-sans text-[10px] text-ink/55" data-id="element-153" data-mp="qvy4nrloxey6">
              Email address
            </label>
            <input id="email" name="email" type="email" value={email} autoComplete="email" placeholder="you@example.com" aria-invalid={Boolean(error)} aria-describedby={error ? 'email-error' : 'email-hint'} onChange={event => {
            setEmail(event.target.value);
            if (error) setError(null);
          }} className={`${fieldClass} mt-2 ${error ? 'border-[#E0806B] focus:border-[#E0806B]' : 'border-rule focus:border-ink'}`} data-id="element-154" data-mp="lvtpq60fksa2" />
            {error ? <p id="email-error" role="alert" className="mt-2 font-sans text-[12px] leading-relaxed text-[#E0806B]" data-id="element-155" data-mp="1scow2w33pk6h">
                {error}
              </p> : <p id="email-hint" className="mt-2 font-sans text-[12px] text-ink/40" data-id="element-156" data-mp="183fmkzx8vlbo">
                So we can send you the new packs. We do not pass it on.
              </p>}
          </div>

          <div className="mt-8" data-id="element-157" data-mp="z53lu52nz75i">
            <label htmlFor="code" className="smallcaps block font-sans text-[10px] text-ink/55" data-id="element-158" data-mp="z5vjrmjqhgyy">
              Upgrade code <span className="tracking-normal normal-case text-ink/35" data-id="element-159" data-mp="1quotr2jjly03">(optional)</span>
            </label>
            <input id="code" name="code" type="text" value={code} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="Leave empty" aria-describedby="code-hint" onChange={event => setCode(event.target.value)} className={`${fieldClass} mt-2 border-rule uppercase tracking-[0.12em] focus:border-ink`} data-id="element-160" data-mp="1vti9z2q7s33y" />
            <p id="code-hint" className="mt-2 font-sans text-[12px] text-ink/40" data-id="element-161" data-mp="350t7813f3h4">
              Only if you added the Creator Pack or the Pro Workshop to your order. It is in your
              receipt email. Everyone else can leave this blank.
            </p>
          </div>

          <button type="submit" className="mt-10 inline-flex w-full items-center justify-between gap-3 bg-green px-5 py-3.5 font-sans text-[13px] text-onaccent transition-colors duration-150 ease-out hover:bg-[#33684D] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-ground sm:w-auto" data-id="element-162" data-mp="cwy6qlfkq7j0">
            <span data-id="element-163" data-mp="3q7ng22myuae8">Enter The Workshop</span>
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-164" data-mp="3lankilnqsq1d" />
          </button>
        </form>
      </div>

      <footer className="border-t border-rule pt-5" data-id="element-165" data-mp="15i7upn6indvz">
        <p className="font-sans text-[13px] text-ink/45" data-id="element-166" data-mp="10l7nva1ue73t">
          Trouble getting in? Reply to your order email and we will help.
        </p>
      </footer>
    </main>;
}